import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { memo, useCallback, useMemo, useState } from 'react'

import { PartnerFlagRequestStatus } from 'clickNgo/entities/partner/models'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { DatePicker } from 'core-components/library/datepicker'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { ConfirmDialog } from 'shared/components/confirmDialog'
import { List } from 'shared/components/list'

import { partnerFlagsTranslation } from '../../../../translation'

import { DATE_PICKER_MODE, DEFAULT_DATE, UNPROCESSED_STATUS } from './configs'
import { validatePartnerFlag } from './helpers/validatePartnerFlag'
import { IPartnerFlagContentProps } from './models'

import styles from './PartnerFlagContent.module.scss'

export const PartnerFlagContent = memo<IPartnerFlagContentProps>(
  ({ partnerFlag, isDisabled, isReadOnly, onDelete, onUpdate }) => {
    const [endDate, setEndDate] = useState<Date>(partnerFlag.endDate)
    const [startDate, setStartDate] = useState<Date>(partnerFlag.startDate)
    const [isDeleteConfirmDialogVisible, setIsDeleteConfirmDialogVisible] = useState(false)

    const { isApplyDisabled, isModified, messages } = useMemo(
      () => validatePartnerFlag({ partnerFlag, endDate }),
      [endDate, partnerFlag]
    )

    const hasDoneStatus = useMemo(() => {
      return partnerFlag.status === PartnerFlagRequestStatus.Done
    }, [partnerFlag.status])

    const isDeleteRequestVisible = Boolean(
      partnerFlag.status && !UNPROCESSED_STATUS.includes(partnerFlag.status)
    )
    const signatureDate = useMemo(
      () => (partnerFlag.signatureDate ? getFormatDate(partnerFlag.signatureDate, 'full') : '-'),
      [partnerFlag.signatureDate]
    )

    const handleOnDeletePartnerFlagRequestConfirm = useCallback(() => {
      onDelete(partnerFlag)
      setIsDeleteConfirmDialogVisible(false)
    }, [onDelete, partnerFlag])

    const handleOnDeletePartnerFlagRequestReject = useCallback(() => {
      setIsDeleteConfirmDialogVisible(false)
    }, [])

    const handleOnApplyButtonClick = useCallback(() => {
      const preparedPartnerFlag = {
        ...partnerFlag,
        startDate,
        endDate,
      }
      onUpdate(preparedPartnerFlag)
    }, [endDate, onUpdate, partnerFlag, startDate])

    const handleOnDeleteButtonClick = useCallback(() => {
      setIsDeleteConfirmDialogVisible(true)
    }, [])

    return (
      <>
        <List.Label className={styles.flag}>
          <div className={styles.field}>
            <div className={styles.label}>
              <Translation text={partnerFlagsTranslation.content.field.codeName} span />
            </div>
            <div>{partnerFlag.codeName || partnerFlag.code || '-'}</div>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>
              <Translation text={partnerFlagsTranslation.content.field.code} span />
            </div>
            <div>{partnerFlag.code || '-'}</div>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>
              <Translation text={partnerFlagsTranslation.content.field.signatureDate} span />
            </div>
            <div>{signatureDate}</div>
          </div>
          <RenderWhen condition={Boolean(partnerFlag.comment)}>
            <div className={styles.field}>
              <div className={styles.label}>
                <Translation text={partnerFlagsTranslation.content.field.comment} span />
              </div>
              <div>{partnerFlag.comment}</div>
            </div>
          </RenderWhen>
          <RenderWhen condition={Boolean(messages.length) && isModified}>
            <InfoBlock text={messages} type={InfoBlockType.Warning} />
          </RenderWhen>
          <RenderWhen condition={!isReadOnly && hasDoneStatus}>
            <InfoBlock
              text={<Translation text={partnerFlagsTranslation.content.statusWarning} />}
              type={InfoBlockType.Warning}
            />
          </RenderWhen>
          <div className={styles.bottom}>
            <div className={styles.dates}>
              <DatePicker
                disabled
                mode={DATE_PICKER_MODE}
                value={startDate}
                label={<Translation text={partnerFlagsTranslation.content.field.startDate} span />}
                onChange={setStartDate}
              />
              <DatePicker
                mode={DATE_PICKER_MODE}
                value={endDate}
                label={<Translation text={partnerFlagsTranslation.content.field.endDate} span />}
                onChange={setEndDate}
                fromDate={DEFAULT_DATE}
                disabled={isDisabled || hasDoneStatus}
              />
              <RenderWhen condition={!isReadOnly}>
                <Button disabled={isApplyDisabled || isDisabled} onClick={handleOnApplyButtonClick}>
                  <Translation text={partnerFlagsTranslation.content.btn.apply} span />
                </Button>
              </RenderWhen>
            </div>
            <RenderWhen condition={!isReadOnly && isDeleteRequestVisible}>
              <Button disabled={isDisabled} color="warning" onClick={handleOnDeleteButtonClick}>
                <Translation text={partnerFlagsTranslation.content.btn.deleteRequest} span />
              </Button>
            </RenderWhen>
          </div>
        </List.Label>
        <ConfirmDialog
          confirmBtnText={partnerFlagsTranslation.content.confirm.delete.btn.apply}
          content={partnerFlagsTranslation.content.confirm.delete.content}
          isVisible={isDeleteConfirmDialogVisible}
          onConfirm={handleOnDeletePartnerFlagRequestConfirm}
          title={partnerFlagsTranslation.content.confirm.delete.title}
          onReject={handleOnDeletePartnerFlagRequestReject}
          rejectBtnText={partnerFlagsTranslation.content.confirm.delete.btn.reject}
          size="sm"
        />
      </>
    )
  }
)

PartnerFlagContent.displayName = 'PartnerFlagContent'
